/**
 * Pick a letter to start from: what ships, or what this office has saved.
 *
 * Applying COPIES. Nothing here links a campaign to a template afterwards -
 * `templateId` is recorded as provenance and nothing more. A live reference
 * would mean editing a template rewrites letters that have already gone out,
 * which is the one thing the send ledger exists to make impossible.
 *
 * Applying replaces whatever body is there. The caller is the one that asks for
 * confirmation, since only it knows whether there is anything to lose.
 */
import { useEffect, useState } from "react";
import { LayoutTemplate, Loader2, Sparkles } from "lucide-react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@wa/ui";
import { useT } from "../../../../i18n/I18nProvider";
import { getTemplate, listTemplates, type TemplateRow } from "../../../../lib/marketingTemplates";
import {
  instantiate,
  parseDesign,
  PRESETS,
  renderEmailHtml,
  renderEmailText,
  type EmailDesign,
  type PresetLocale,
} from "../../../../emailBuilder";
import { TemplateCard } from "../templates/TemplateCard";

export interface AppliedLetter {
  design: EmailDesign | null;
  bodyHtml: string;
  bodyText: string;
  subject: string;
  /** Set when the letter came from a saved template, so the campaign can record
   *  where it started. Absent for a preset - those are not rows. */
  templateId?: string | null;
}

export function TemplatePicker({
  open,
  locale,
  onClose,
  onApply,
}: {
  open: boolean;
  locale: PresetLocale;
  onClose: () => void;
  onApply: (letter: AppliedLetter) => void;
}) {
  const t = useT();
  const [rows, setRows] = useState<TemplateRow[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    listTemplates()
      .then((res) => setRows(res.rows))
      .catch(() => setRows([]));
  }, [open]);

  /**
   * Apply a letter that ships with the module - or the office's edit of it.
   *
   * An edited preset is a real row, so it is applied through the row path: that
   * fetches the stored bodies, and it records `templateId` so the campaign can
   * say where its wording came from. Reading the bundle instead would quietly
   * hand out the shipped wording that the operator has already replaced, and
   * the edit would look like it had not saved.
   */
  function applyPreset(id: string) {
    const preset = PRESETS.find((p) => p.id === id);
    if (!preset) return;
    const edited = (rows ?? []).find((r) => r.presetId === id);
    if (edited) {
      void applySaved(edited);
      return;
    }
    onApply({ ...instantiate(preset, locale), templateId: null });
  }

  /**
   * A saved template's full record, then applied.
   *
   * The list carries the design but not the bodies, and a template written
   * straight in HTML has no design at all - so the bodies are fetched rather
   * than re-rendered from the design, and only regenerated when the row has a
   * design and the stored HTML is missing.
   */
  async function applySaved(row: TemplateRow) {
    setBusy(row.id);
    try {
      const { template } = await getTemplate(row.id);
      const design = parseDesign(template.design);
      onApply({
        design,
        bodyHtml: template.bodyHtml || (design ? renderEmailHtml(design) : ""),
        bodyText: template.bodyText || (design ? renderEmailText(design) : ""),
        subject: template.subject,
        templateId: template.id,
      });
    } finally {
      setBusy(null);
    }
  }

  // An edited shipped letter appears on the presets tab, in the place of the
  // letter it replaces. Listing it here too would offer the same wording twice,
  // and a letter taken off the shelf would come back through the side door.
  const saved = (rows ?? []).filter((r) => !r.presetId);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <LayoutTemplate className="size-4 text-primary" /> {t("marketing.letter.pickTitle")}
          </DialogTitle>
          <DialogDescription>{t("marketing.letter.pickIntro")}</DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="presets">
          <TabsList>
            <TabsTrigger value="presets" className="gap-1.5">
              <Sparkles className="size-3.5" /> {t("marketing.templates.presetsTitle")}
            </TabsTrigger>
            <TabsTrigger value="saved">{t("marketing.templates.savedTitle")}</TabsTrigger>
          </TabsList>

          <TabsContent value="presets" className="mt-4">
            <div className="grid max-h-[55vh] gap-4 overflow-y-auto p-0.5 sm:grid-cols-2 lg:grid-cols-3">
              {PRESETS.filter((p) => !(rows ?? []).some((r) => r.presetId === p.id && r.hidden)).map((preset) => {
                const edited = (rows ?? []).find((r) => r.presetId === preset.id);
                return (
                  <TemplateCard
                    key={preset.id}
                    name={edited?.name ?? t(`marketing.preset.${preset.key}.name`)}
                    description={edited?.description ?? t(`marketing.preset.${preset.key}.description`)}
                    subject={edited?.subject ?? preset.subject[locale]}
                    design={edited?.design ?? preset.build(locale)}
                    tag={edited ? t("marketing.templates.edited") : undefined}
                    actions={
                      <Button size="sm" disabled={busy === edited?.id} onClick={() => applyPreset(preset.id)}>
                        {t("marketing.letter.apply")}
                      </Button>
                    }
                  />
                );
              })}
            </div>
          </TabsContent>

          <TabsContent value="saved" className="mt-4">
            {rows === null ? (
              <div className="flex justify-center py-12 text-muted-foreground">
                <Loader2 className="size-5 animate-spin" />
              </div>
            ) : saved.length === 0 ? (
              <p className="py-12 text-center text-sm text-muted-foreground">{t("marketing.templates.empty")}</p>
            ) : (
              <div className="grid max-h-[55vh] gap-4 overflow-y-auto p-0.5 sm:grid-cols-2 lg:grid-cols-3">
                {saved.map((row) => (
                  <TemplateCard
                    key={row.id}
                    name={row.name}
                    description={row.description ?? ""}
                    subject={row.subject}
                    locale={row.locale}
                    design={row.design}
                    actions={
                      <Button size="sm" disabled={busy === row.id} onClick={() => void applySaved(row)}>
                        {busy === row.id && <Loader2 className="size-3.5 animate-spin" />}
                        {t("marketing.letter.apply")}
                      </Button>
                    }
                  />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
